import Modal from '@berlitzplatforms/micro.ui.modal'
import Spacer from '@berlitz/spacer'
import Spinner from '@berlitz/spinner'
import PortalBerlitzUserEvents from '@classes/BerlitzUserEvents'
import DateHeader from '@components/DateHeader'
import Datepicker from '@components/Datepicker'
import EmptyScheduledLessons from '@components/EmptyScheduledLessons'
import Fileupload from '@components/Fileupload'
import LessonCutOff from '@components/LessonCutOff'
import { usePortalFeatures } from '@components/RoleBasedView'
import { GroupClassList } from '@interfaces/MyLesson'
import { Attachment } from '@interfaces/ScheduleSession'
import { ProfileProps } from '@interfaces/UserProfile'
import { EventAttendeeContext } from '@layouts/Events/context'
import { LessonDate } from '@layouts/MySchedule/Layout/interface'
import { SchedBox } from '@layouts/MySchedule/Layout/style'
import { uniqBy } from 'lodash'
import moment from 'moment'
import React, { useState } from 'react'
import { Col, Row } from 'react-styled-flexboxgrid'
import Lessons from '../Lessons'
import { LayoutProp } from './interface'
import Loader from './Loader'
import { Container, DatePickerCol, SpinnerBox } from './style'

const Layout: React.FC<LayoutProp & ProfileProps> = ({
  data,
  date,
  instructor,
  instructorName,
  startDate,
  endDate,
  loading,
  profile,
  setDate,
  onMonthChange,
  lessonDates: propLessonDates = [],
  fetchingLessonDates,
}) => {
  const { state } = usePortalFeatures({
    configId: 'c::sidenav::events',
  })
  const userEventCtx = React.useContext(EventAttendeeContext)
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [openFileModal, setOpenFileModal] = useState(false)
  const [note, setNote] = useState<string>('')
  const [openNoteModal, setOpenNoteModal] = useState(false)
  const [lessonDates, setLessonDates] = React.useState<LessonDate[]>(propLessonDates)
  const [eventGroupClassList, setEventGroupClassList] = React.useState<GroupClassList[]>([])

  React.useEffect(() => {
    if (state.allowed) {
      const UserEvents = new PortalBerlitzUserEvents(userEventCtx.data?.getEventAttendeev2, profile?.Timezone)
      setLessonDates(UserEvents.getCalendarDates(uniqBy(propLessonDates, 'date')))
      setEventGroupClassList(UserEvents.getMyLessonEventsByDate(date))
    } else {
      setLessonDates(propLessonDates)
    }
  }, [state.allowed, userEventCtx.data?.getEventAttendeev2, profile?.Timezone, propLessonDates, date])

  return (
    <SchedBox>
      <Row>
        <Col xs={12} lg={3}>
          <DateHeader date={date} selected />
        </Col>
        <Col xs={false} lg={6} />
        <DatePickerCol xs={false} lg={3}>
          {(fetchingLessonDates || userEventCtx.loading) && (
            <SpinnerBox>
              <Spinner size="sm" />
            </SpinnerBox>
          )}

          <Datepicker
            label=""
            selected={date.startOf('day').toDate()}
            dateFormat="dd MMMM yyyy"
            onSelect={setDate}
            onMonthChange={onMonthChange}
            isClearable={false}
            shouldCloseOnSelect={false}
            openToDate={date.clone().startOf('day').toDate()}
            popperPlacement="bottom-end"
            popperModifiers={{
              flip: { behavior: ['bottom'] },
              preventOverflow: { enabled: false },
              hide: { enabled: false },
            }}
            dayClassName={(classDate: string) => {
              const lessonDay = lessonDates.find((lessonDate) =>
                moment(lessonDate.date, 'YYYY-MM-DD').isSame(moment(classDate, moment.defaultFormat))
              )

              return lessonDay && `react-datepicker__day--highlighted-count-${lessonDay.numberOfLessons}`
            }}
            className="popped-over"
          />
        </DatePickerCol>

        <Spacer size="md" />

        {loading && <Loader />}

        {!loading && (
          <>
            <Col xs={12} md={12}>
              {(data.GroupClassList.length > 0 || (eventGroupClassList || []).length) > 0 ? (
                <LessonCutOff CurrentDate={date} loading={loading}>
                  {(isCutOff: boolean) => (
                    <Container>
                      <Lessons
                        data={{
                          ...data,
                          GroupClassList: [...(data.GroupClassList || []), ...(eventGroupClassList || [])],
                        }}
                        date={date}
                        instructor={instructor}
                        instructorName={instructorName}
                        startDate={startDate}
                        endDate={endDate}
                        profile={profile}
                        isCutOff={isCutOff}
                        setAttachments={setAttachments}
                        setOpenFileModal={setOpenFileModal}
                        setOpenNoteModal={setOpenNoteModal}
                        setNote={setNote}
                        recordId={userEventCtx.RecordId}
                      />
                      <Fileupload
                        open={openFileModal}
                        onSave={() => {}}
                        onClose={() => setOpenFileModal(false)}
                        center
                        hidden
                        files={attachments}
                      />
                      <Modal open={openNoteModal} onClose={() => setOpenNoteModal(false)}>
                        {note}
                      </Modal>
                    </Container>
                  )}
                </LessonCutOff>
              ) : (
                <EmptyScheduledLessons />
              )}
            </Col>
          </>
        )}
      </Row>
    </SchedBox>
  )
}

export default Layout
